/**
 * SharedLiving facility predicate (Phase 11 UI sprint, UI-068 / UI-072).
 *
 * The backend Phase 11 introduced a per-facility `is_sharedliving_facility`
 * flag (1 = pharmacy-admin-flagged as an SL care site). Patients living at
 * such a facility are SL-eligible and get nurse / case-manager dropdowns; they
 * also receive the "SL" badge in patient lists.
 *
 * SL-eligibility is OR-based per Locked Design Decision UD2:
 *   - patient.facility.is_sharedliving_facility === 1, OR
 *   - patient has at least one home caregiver (athome_caregivers.length > 0).
 *
 * Many code paths only have the patient row (no nested facility object), so
 * this helper looks for the flag at any reasonable depth — `patient.is_sharedliving_facility`,
 * `patient.facility?.is_sharedliving_facility`, or a passed-in facility row.
 */

const TRUTHY = new Set([1, '1', true, 'true']);

const isTrue = v => TRUTHY.has(v) || TRUTHY.has(Number(v));

/**
 * True when the given facility row has is_sharedliving_facility = 1.
 * Accepts the facility object directly, or a patient row that has the flag
 * surfaced (Patient.getByGroupId / getByFacilityId LEFT JOIN — see backend
 * Open Item 2 resolution).
 */
export const isSharedLivingFacility = (facilityOrPatient) => {
  if (!facilityOrPatient) return false;

  // Direct facility row
  if (Object.prototype.hasOwnProperty.call(facilityOrPatient, 'is_sharedliving_facility')) {
    return isTrue(facilityOrPatient.is_sharedliving_facility);
  }
  // Patient row with nested facility object
  if (facilityOrPatient.facility && Object.prototype.hasOwnProperty.call(facilityOrPatient.facility, 'is_sharedliving_facility')) {
    return isTrue(facilityOrPatient.facility.is_sharedliving_facility);
  }
  return false;
};

/**
 * True when a patient is SharedLiving-eligible per UD2.
 * Use this for conditional renders (nurse/CM dropdowns, "SL" badge).
 *
 * Accepts either the full athome_caregivers array (present on detail responses)
 * or a flat `athome_caregivers_count` (present on list-view rows where the
 * array isn't expanded).
 */
export const isSharedLivingPatient = (patient) => {
  if (!patient) return false;
  if (isSharedLivingFacility(patient)) return true;
  const caregivers = patient.athome_caregivers;
  if (Array.isArray(caregivers) && caregivers.length > 0) return true;
  if (typeof patient.athome_caregivers_count === 'number' && patient.athome_caregivers_count > 0) return true;
  return false;
};

export default isSharedLivingFacility;
